import { ErrorState, Loading, PageHead, Select, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CRM_KEYS, usePipelines, type Deal } from '../crm/data';
import { NewDealDialog } from '../crm/forms';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Pipeline board. Drag a card to another stage (mouse), or use the stage selector on the card (keyboard, touch and
 * screen readers). Moving to a won/lost stage closes the deal.
 */
export function DealsPage() {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const fmt = useFormat();
  const qc = useQueryClient();
  const pipes = usePipelines();
  const pipeline = pipes.data?.items[0];
  const deals = useQuery({
    queryKey: ['deals', pipeline?.id],
    queryFn: () => api<{ items: Deal[] }>(`/v1/deals?pipelineId=${pipeline!.id}`),
    enabled: !!pipeline,
  });
  const [dialogStage, setDialogStage] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const move = useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) =>
      api(`/v1/deals/${id}`, { method: 'PATCH', body: JSON.stringify({ stageId }) }),
    onMutate: async ({ id, stageId }) => {
      // Optimistic: the card moves immediately; a failure restores the server state.
      const key = ['deals', pipeline?.id];
      const prev = qc.getQueryData<{ items: Deal[] }>(key);
      if (prev) qc.setQueryData(key, { items: prev.items.map((d) => (d.id === id ? { ...d, stageId } : d)) });
      return { prev };
    },
    onError: (_e, _v, c) => c?.prev && qc.setQueryData(['deals', pipeline?.id], c.prev),
    onSettled: () => CRM_KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
  const ar = i18n.language === 'ar';
  if (pipes.isLoading || deals.isLoading) return <Loading />;
  if (pipes.isError || !pipeline) return <ErrorState error={pipes.error} />;
  const editable = can('deals:write');
  const onDrop = (stageId: string) => (e: DragEvent) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData('text/deal');
    if (id) move.mutate({ id, stageId });
  };

  return (
    <>
      <PageHead
        eyebrow={ar ? pipeline.nameAr : pipeline.nameEn}
        title={t('crm.deals.title')}
        subtitle={t('crm.deals.subtitle')}
        actions={
          editable && (
            <button className="btn btn-grad" onClick={() => setDialogStage('')} data-testid="add-deal">
              <Plus size={16} />
              {t('crm.deals.new')}
            </button>
          )
        }
      />
      <div className="board" data-testid="deal-board">
        {pipeline.stages.map((s) => {
          const cards = (deals.data?.items ?? []).filter((d) => d.stageId === s.id);
          const sums = new Map<string, number>();
          for (const d of cards) sums.set(d.currency, (sums.get(d.currency) ?? 0) + Number(d.amountMinor));
          const total =
            [...sums].map(([cur, v]) => fmt.money(v, cur)).join(' + ') ||
            fmt.money(0, cards[0]?.currency ?? 'AED');
          return (
            <section
              key={s.id}
              className={`board-col${over === s.id ? ' drop' : ''}`}
              aria-label={ar ? s.nameAr : s.nameEn}
              data-stage={s.key}
              onDragOver={(e) => {
                if (!editable) return;
                e.preventDefault();
                setOver(s.id);
              }}
              onDragLeave={() => setOver(null)}
              onDrop={onDrop(s.id)}
            >
              <header>
                <strong>
                  <span>{ar ? s.nameAr : s.nameEn}</span>
                  <span className="faint">{s.kind === 'open' ? `${s.probability}%` : ''}</span>
                </strong>
                <div className="sum">{t('crm.deals.total', { count: cards.length, value: total })}</div>
              </header>
              <div className="board-cards">
                {cards.map((d) => (
                  <article
                    key={d.id}
                    className="deal-card"
                    draggable={editable}
                    onDragStart={(e) => e.dataTransfer.setData('text/deal', d.id)}
                    data-testid="deal-card"
                  >
                    <div dir="auto" style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>
                      {d.title}
                    </div>
                    <div className="amt">{fmt.money(Number(d.amountMinor), d.currency)}</div>
                    <div className="who" dir="auto">
                      {d.accountId ? <Link to={`/companies/${d.accountId}`}>{d.accountName}</Link> : null}
                      {d.accountId && d.personId ? ' · ' : null}
                      {d.personId ? <Link to={`/people/${d.personId}`}>{d.personName}</Link> : null}
                    </div>
                    {editable && (
                      <label>
                        <span className="sr-only">{t('crm.deals.moveTo')}</span>
                        <Select
                          value={d.stageId}
                          onChange={(v) => move.mutate({ id: d.id, stageId: v })}
                          aria-label={t('crm.deals.field.stage')}
                          options={pipeline.stages.map((o) => ({
                            value: o.id,
                            label: ar ? o.nameAr : o.nameEn,
                          }))}
                        />
                      </label>
                    )}
                  </article>
                ))}
                {editable && s.kind === 'open' && (
                  <button className="btn btn-ghost btn-sm" onClick={() => setDialogStage(s.id)}>
                    <Plus size={14} />
                    {t('common.add')}
                  </button>
                )}
              </div>
            </section>
          );
        })}
      </div>
      {dialogStage !== null && (
        <NewDealDialog open onClose={() => setDialogStage(null)} stageId={dialogStage || undefined} />
      )}
    </>
  );
}
